import { decodeUnknown } from '@effect/schema/Schema';
import { Effect, RequestResolver, pipe } from 'effect';
import * as Url from '../../../../url';
import { HttpServiceInterface } from '../../../../../services/http';
import { PaginatedBody, decodeParticles } from '../schema';
import { GetParticles, GetParticlesError } from './requests';

const PAGE_SIZE = 200;

// Get a single page defined by cursor, or first page if no cursor,
const fetchPage = (
    cursor: string | undefined,
    config: {
        http: HttpServiceInterface;
        gluonUrl: Url.Url;
        tenant: string;
    },
) => {
    return config.http
        .get(
            Url.join(config.gluonUrl, `/api/v1/${config.tenant}/particles`),
            {},
            { cursor, limit: PAGE_SIZE },
        )
        .pipe(
            Effect.andThen(r => decodeUnknown(PaginatedBody)(r.body)),
            Effect.mapError(error => new GetParticlesError({ originalError: error })),
        );
};

const shouldContinue = (response: any) =>
    response.particlesForProducts &&
    Object.entries(response.particlesForProducts).length === PAGE_SIZE &&
    'next' in response;

// Recursively get page by page until none left to fetch.
const getAllPages = (config: {
    http: HttpServiceInterface;
    gluonUrl: Url.Url;
    tenant: string;
}): Effect.Effect<Record<string, unknown>, GetParticlesError> =>
    pipe(
        fetchPage(undefined, config),
        Effect.flatMap(response => {
            if (shouldContinue(response)) {
                return Effect.iterate(
                    {
                        cursor: response.next,
                        accumulator: response.particlesForProducts,
                        config,
                    },
                    {
                        while: state => state.cursor !== undefined,
                        body: state =>
                            pipe(
                                fetchPage(state.cursor, state.config),
                                Effect.map(response => {
                                    const newAccumulator = Object.assign(
                                        state.accumulator,
                                        response.particlesForProducts,
                                    );

                                    return {
                                        cursor: shouldContinue(response)
                                            ? response.next
                                            : undefined,
                                        accumulator: newAccumulator,
                                        config: state.config,
                                    };
                                }),
                            ),
                    },
                ).pipe(Effect.map(res => res.accumulator));
            } else {
                // Pagination not needed
                return Effect.succeed(response.particlesForProducts);
            }
        }),
        Effect.mapError(e => new GetParticlesError({ originalError: e })),
    );

export const GetAllParticlesPaginatedResolver = (http: HttpServiceInterface, gluonUrl: Url.Url) =>
    RequestResolver.fromEffect((request: GetParticles) =>
        getAllPages({
            http,
            gluonUrl,
            tenant: request.tenant,
        }).pipe(
            Effect.andThen(particlesForProducts => {
                const res = {
                    particlesForProducts,
                };
                return decodeParticles(res);
            }),
        ),
    );
